import './stepperProgressVertical.css';
import { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "../../../../contexts/TranslationContext";
import RejectionIcon from '../../../../assets/icons/rejectionIcon.svg';

export default function StepperProgressVertical({ request, appIsV1, onViewOffer, onViewContractSigning }) {
    const { translate } = useTranslation();
    const [currentStep, setCurrentStep] = useState(null);

    // Step configuration
    const steps = [
        {
            id: 1,
            stepTitle: translate('SUBMIT'),
            status: 'S',
            rejected: request.APPLICATION_STATUS === 'S' && request.QARAR_STATUS === 'REJECTED'
        },
        {
            id: 2,
            stepTitle: translate('STEP_OFFER'),
            status: 'O',
            rejected: request.APPLICATION_STATUS === 'O' && request.QARAR_STATUS === 'REJECTED'
        },
        {
            id: 3,
            stepTitle: translate('STEP_CONTRACT'),
            status: 'C',
            rejected: request.APPLICATION_STATUS === 'C' && request.QARAR_STATUS === 'REJECTED'
        },
        {
            id: 4,
            stepTitle: translate('DISBURSEMENNT'),
            status: 'DI',
            rejected: request.APPLICATION_STATUS === 'DI' && request.QARAR_STATUS === 'REJECTED'
        }
    ];

    // Determine current step based on request status
    useEffect(() => {
        if (!request) return;

        if ((request.APPLICATION_STATUS === 'S' || request.APPLICATION_STATUS === 'C') || request.IVR_STATUS === 'PENDING') setCurrentStep(0);
        if (request.OFFER_STATUS) {
            if (appIsV1) {
                setCurrentStep(1);
            } else if (request.IVR_STATUS === 'ACCEPTED') {
                setCurrentStep(1);
            }
        }
        if (request.CONTRACT_STATUS === 'SIGNED' || request.CONTRACT_STATUS === 'GENERATED' || request.CONTRACT_STATUS === 'ACCEPTED') setCurrentStep(2);
        if (request.OFFER_STATUS === 'REJECTED' || request.OFFER_STATUS === 'EXPIRED') setCurrentStep(1);
        if (request.CONTRACT_STATUS === 'REJECTED' || request.CONTRACT_STATUS === 'EXPIRED') setCurrentStep(2);
        if (request.CONTRACT_STATUS === 'SIGNED' && request.DISBURSED_ON) setCurrentStep(3);
    }, [request, appIsV1]);

    // Get appropriate offer date
    const getOfferDate = () => {
        return request.OFFER_STATUS === 'GENERATED'
            ? request.OFFER_DATE
            : request.OFFER_ACCEPT_DATE;
    };

    // Get appropriate contract date
    const getContractDate = () => {
        if (request.CONTRACT_STATUS === 'GENERATED') return request.CONTRACT_DATE;
        if (request.CONTRACT_STATUS === 'SIGNED') return request.CONTRACT_SIGN_DATE;
        return false;
    };

    // Get date for specific step
    const getStepDate = (stepId) => {
        switch (stepId) {
            case 0:
                if (!appIsV1 && request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'PENDING') {
                    return request.CREATED_ON;
                }
                return request.SUBMITED_ON;
            case 1: return getOfferDate();
            case 2: return getContractDate();
            case 3: return request.DISBURSED_ON;
            default: return false;
        }
    };

    // Get status label for specific step
    const getStepLabel = (stepId) => {
        switch (stepId) {
            case 0:
                if (appIsV1) { //if V1
                    if (request.APPLICATION_STATUS === 'S') {
                        return 'ACCEPTED';
                    }
                }
                // V2 IVR Rejection label
                else if (request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'REJECTED') {
                    return 'REJECTED';
                }
                else if (request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'PENDING') {
                    return 'PENDING';
                }
                else if (request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'ACCEPTED') {
                    return 'ACCEPTED';
                }
                return request.OFFER_STATUS;
            case 1:
                if (request.OFFER_STATUS === 'GENERATED' || request.OFFER_STATUS === 'PENDING' || request.OFFER_STATUS === 'ACCEPTED')
                    return request.OFFER_STATUS;
                return request.CONTRACT_STATUS;
            case 2:
                return (request.CONTRACT_STATUS === 'SIGNED' || request.CONTRACT_STATUS === 'GENERATED' || request.CONTRACT_STATUS === 'ACCEPTED') ? request.CONTRACT_STATUS : null;
            case 3:
                return (request.CONTRACT_STATUS === 'SIGNED' && request.DISBURSED_ON) ? 'DISBURSED' : null;
            default: return null;
        }
    };

    // Check if step is rejected (for styling)
    const isStepRejected = (index) => {
        // V2 IVR Rejection - Submit step (index 0)
        if (!appIsV1 && index === 0 && request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'REJECTED') {
            return true;
        }

        if (index === 1 && (request.OFFER_STATUS === 'REJECTED' || request.OFFER_STATUS === 'EXPIRED')) {
            return true;
        }
        if (index === 2 && (request.CONTRACT_STATUS === 'REJECTED' || request.CONTRACT_STATUS === 'EXPIRED')) {
            return true;
        }
        return false;
    };

    const isStepPending = (index) => {
        if (index === 0 && request.IVR_STATUS === 'PENDING') {
            return true;
        }
        return false;
    };

    // Format date for display
    const formatStepDate = (stepId) => {
        const date = getStepDate(stepId);
        return date ? moment(date).format('DD/MM/YYYY') : '';
    };

    // Helper to get human label (Accepted/Pending/Rejected) per step
    const getDisplayStatus = (index) => {
        if (isStepRejected(index)) return translate('STEPPER.STATUS.REJECTED');

        // Submit step pending for V2 IVR
        if (!appIsV1 && index === 0 && request.IVR_STATUS === 'PENDING') return translate('STEPPER.STATUS.PENDING');

        // Contract step (index 2): Show "Signed" when CONTRACT_STATUS is SIGNED
        if (index === 2 && request.CONTRACT_STATUS === 'SIGNED') {
            return translate('STEPPER.STATUS.SIGNED');
        }

        const raw = getStepLabel(index);
        if (!raw) return null;
        if (raw === 'PENDING') return translate('STEPPER.STATUS.PENDING');
        if (raw === 'GENERATED') return translate('STEPPER.STATUS.PENDING');
        if (raw === 'SIGNED' || raw === 'ACCEPTED') return translate('STEPPER.STATUS.ACCEPTED');
        if (raw === 'DISBURSED') return translate('STEPPER.STATUS.DISBURSED');
        return raw;
    };

    const getStatusPillClass = (index) => {
        const status = getDisplayStatus(index);
        if (!status) return '';
        // Check against translated values
        const rejectedText = translate('STEPPER.STATUS.REJECTED');
        const pendingText = translate('STEPPER.STATUS.PENDING');
        const acceptedText = translate('STEPPER.STATUS.ACCEPTED');
        const disbursedText = translate('STEPPER.STATUS.DISBURSED');
        const signedText = translate('STEPPER.STATUS.SIGNED');

        if (status === rejectedText) return 'status-pill rejected';
        if (status === pendingText) return 'status-pill pending';
        if (status === acceptedText || status === disbursedText || status === signedText) return 'status-pill accepted';
        return 'status-pill';
    };

    const getStepDescription = (index) => {
        // Step 0: Submit
        if (index === 0) {
            if (isStepRejected(0)) {
                return translate('STEPPER.DESC.SUBMIT_REJECTED');
            }
            if (!appIsV1 && request.IVR_STATUS === 'PENDING') {
                return translate('STEPPER.DESC.SUBMIT_PENDING');
            }
            if (!appIsV1 && request.IVR_STATUS === 'ACCEPTED') {
                return translate('STEPPER.DESC.SUBMIT_ACCEPTED');
            }
            if (appIsV1 && request.APPLICATION_STATUS === 'S') {
                return translate('STEPPER.DESC.SUBMIT_ACCEPTED');
            }
            return translate('STEPPER.DESC.SUBMIT_DEFAULT');
        }

        // Step 1: Offer
        if (index === 1) {
            if (isStepRejected(1)) {
                return translate('STEPPER.DESC.OFFER_REJECTED');
            }
            if (request.OFFER_STATUS === 'ACCEPTED') {
                return translate('STEPPER.DESC.OFFER_ACCEPTED');
            }
            if (request.OFFER_STATUS === 'GENERATED') {
                return translate('STEPPER.DESC.OFFER_DEFAULT');
            }
            if (request.OFFER_STATUS === 'PENDING') {
                return translate('STEPPER.DESC.OFFER_PENDING');
            }
            return translate('STEPPER.DESC.OFFER_DEFAULT');
        }

        // Step 2: Contract
        if (index === 2) {
            if (isStepRejected(2)) {
                return translate('STEPPER.DESC.CONTRACT_REJECTED');
            }
            if (request.CONTRACT_STATUS === 'SIGNED') {
                return translate('STEPPER.DESC.CONTRACT_SIGNED');
            }
            if (request.CONTRACT_STATUS === 'GENERATED') {
                return translate('STEPPER.DESC.CONTRACT_DEFAULT');
            }
            return translate('STEPPER.DESC.CONTRACT_DEFAULT');
        }

        // Step 3: Disbursement
        if (index === 3) {
            if (request.DISBURSED_ON) {
                return translate('STEPPER.DESC.DISBURSEMENT_COMPLETED');
            }
            return translate('STEPPER.DESC.DISBURSEMENT_DEFAULT');
        }

        return '';
    };

    const shouldShowOfferCta = (index) => {
        return index === 1 && request.OFFER_STATUS === 'GENERATED' && typeof onViewOffer === 'function';
    };

    const shouldShowContractCta = (index) => {
        return index === 2 && request.CONTRACT_STATUS === 'GENERATED' && typeof onViewContractSigning === 'function';
    };

    const shouldShowViewContractCta = (index) => {
        return index === 2 && request.CONTRACT_STATUS === 'SIGNED' && typeof onViewContractSigning === 'function';
    };

    // Determine if step is completed, current, or future
    const getStepState = (index) => {
        if (index < currentStep) return 'completed';
        if (index === currentStep) {
            if (isStepPending(index)) return 'pending';
            return 'current';
        }
        return 'future';
    };

    // Generate CSS classes for the circle marker
    const getCircleClasses = (index) => {
        const state = getStepState(index);
        const classes = ['step-circle'];

        if (state === 'completed') classes.push('completed');
        if (state === 'current' || state === 'pending') classes.push('current');
        if (isStepRejected(index)) classes.push('rejected');
        if (isStepPending(index)) classes.push('pending');

        return classes.join(' ');
    };

    // Generate CSS classes for the connecting line
    const getLineClasses = (index) => {
        const classes = ['step-line'];

        if (index < currentStep) classes.push('completed');
        if (isStepRejected(index)) classes.push('rejected');
        if (isStepPending(index)) classes.push('pending');

        return classes.join(' ');
    };

    // Render individual step item
    const renderStepItem = (step, index) => {
        const stepDate = formatStepDate(step.id - 1);
        const statusClass = getStatusPillClass(index);
        const displayStatus = getDisplayStatus(index);
        const isLastStep = index === steps.length - 1;

        return (
            <div className="vertical-step-item" key={step.id}>
                <div className="step-indicator">
                    <div className={getCircleClasses(index)}>
                        {getStepState(index) === 'completed' && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3334 4L6.00002 11.3333L2.66669 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                        {(getStepState(index) === 'current' || getStepState(index) === 'pending') && (
                            <div className="inner-dot"></div>
                        )}
                        {getStepState(index) === 'future' && (
                            <div className="inner-dot"></div>
                        )}
                    </div>
                    {!isLastStep && <div className={getLineClasses(index)}></div>}
                </div>

                <div className="step-content">
                    <div className="step-header">
                        <div className="step-title-row">
                            <h3 className="step-title">{step.stepTitle}</h3>
                            {stepDate && <span className="step-date">{stepDate}</span>}
                        </div>
                        {displayStatus && (
                            <div className={statusClass}>{displayStatus}</div>
                        )}
                    </div>

                    <p className="step-description">{getStepDescription(index)}</p>

                    {shouldShowOfferCta(index) && (
                        <button
                            type="button"
                            className="step-action-button"
                            onClick={() => onViewOffer(request)}
                        >
                            View Offer
                        </button>
                    )}

                    {shouldShowContractCta(index) && (
                        <button
                            type="button"
                            className="step-action-button"
                            onClick={() => onViewContractSigning(request)}
                        >
                            Contract Signing
                        </button>
                    )}

                    {shouldShowViewContractCta(index) && (
                        <button
                            type="button"
                            className="step-action-button"
                            onClick={() => onViewContractSigning(request)}
                        >
                            View Contract
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Render rejection message if applicable
    const renderRejectionMessage = () => {
        // V2 IVR Rejection
        if (!appIsV1 && request.APPLICATION_STATUS === 'C' && request.IVR_STATUS === 'REJECTED') {
            return (
                <div className="final-rejection-banner">
                    <div className="rejection-icon">
                        <img src={RejectionIcon} alt="Rejection Icon" />
                    </div>
                    <div className="rejection-content">
                        <h4 className="rejection-title">{translate('REJECTION_REASON')}</h4>
                        <p className="rejection-text">{translate('IVRREJECTED')}</p>
                    </div>
                </div>
            );
        }

        // Regular rejection
        const shouldShowRejection = request.FINAL_STATUS === 'REJECTED' && request.FINAL_REJECTION_REASON;

        if (!shouldShowRejection) return null;

        return (
            <div className="final-rejection-banner">
                <div className="rejection-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 6.66667V10" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 13.3333H10.0083" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="rejection-content">
                    <h4 className="rejection-title">{translate('REJECTION_REASON')}</h4>
                    <p className="rejection-text">{request.FINAL_REJECTION_REASON}</p>
                </div>
            </div>
        );
    };

    return (
        <>
            {renderRejectionMessage()}

            <div className="vertical-stepper-container">
                {steps.map((step, index) => renderStepItem(step, index))}
            </div>
        </>
    );
}
